import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useTheme } from "next-themes";
import {
  ArrowRight,
  Contact,
  CornerDownLeft,
  Handshake,
  History,
  ListTodo,
  Loader2,
  MonitorSmartphone,
  Moon,
  Package,
  Store,
  Sun,
  type LucideIcon,
} from "lucide-react";

import { api } from "../../convex/_generated/api";
import { navItems } from "@/lib/dashboard-data";
import { navIconMap } from "@/components/nav-icons";
import { usePersistedState } from "@/hooks/use-persisted-state";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";

// Global search + jump menu (Cmd/Ctrl+K). Pages and actions filter locally;
// records come from one Convex full-text query (convex/search.ts).
//
//   customers: thamel   -> only businesses     (also contacts, tasks,
//                           products, partners, devices)

type Hit = NonNullable<ReturnType<typeof useQuery<typeof api.search.global>>>[number];
type Kind = Hit["kind"];

const KINDS: Record<Kind, { heading: string; icon: LucideIcon; aliases: string[] }> = {
  account: { heading: "Customers", icon: Store, aliases: ["customer", "customers", "business", "businesses", "account", "accounts", "shop", "shops"] },
  contact: { heading: "Contacts", icon: Contact, aliases: ["contact", "contacts", "person", "people"] },
  task: { heading: "Tasks", icon: ListTodo, aliases: ["task", "tasks", "todo"] },
  product: { heading: "Products", icon: Package, aliases: ["product", "products", "inventory", "stock", "sku"] },
  partner: { heading: "Partners", icon: Handshake, aliases: ["partner", "partners", "referrer"] },
  device: { heading: "Devices", icon: MonitorSmartphone, aliases: ["device", "devices", "serial"] },
};
const KIND_ORDER: Kind[] = ["account", "contact", "task", "product", "partner", "device"];

/** "tasks: renew" -> { kind: "task", text: "renew" }. */
function parseScope(input: string): { kind?: Kind; text: string } {
  const m = /^\s*([a-z]+)\s*:\s*(.*)$/i.exec(input);
  if (m) {
    const word = m[1].toLowerCase();
    const kind = KIND_ORDER.find((k) => KINDS[k].aliases.includes(word));
    if (kind) return { kind, text: m[2] };
  }
  return { text: input.trim() };
}

function useDebounced<T>(value: T, ms: number) {
  const [out, setOut] = React.useState(value);
  React.useEffect(() => {
    const id = window.setTimeout(() => setOut(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return out;
}

/** Bold the parts of `text` that match any word of the query. */
function Highlight({ text, query }: { text: string; query: string }) {
  const words = query
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (words.length === 0) return <>{text}</>;
  const parts = text.split(new RegExp(`(${words.join("|")})`, "i"));
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="bg-transparent font-semibold text-foreground underline decoration-primary/40 underline-offset-2">
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  );
}

const isHit = (x: unknown): x is Hit =>
  typeof x === "object" && x !== null && "kind" in x && "id" in x && "title" in x;
const isHitList = (x: unknown): x is Hit[] => Array.isArray(x) && x.every(isHit);

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { resolvedTheme, setTheme } = useTheme();
  const [input, setInput] = React.useState("");
  const [recent, setRecent] = usePersistedState<Hit[]>("palette-recent", [], isHitList);

  const { kind, text } = parseScope(input);
  const debounced = useDebounced(text, 150);
  const results = useQuery(
    api.search.global,
    open && debounced ? { q: debounced, kind } : "skip",
  );
  const searching = !!text && (debounced !== text || results === undefined);

  const setOpen = (next: boolean) => {
    if (!next) setInput("");
    onOpenChange(next);
  };
  const close = () => setOpen(false);

  const openHit = (hit: Hit) => {
    close();
    setRecent((prev) => [hit, ...prev.filter((h) => h.id !== hit.id)].slice(0, 5));
    switch (hit.kind) {
      case "account":
        navigate({ to: "/customers/$id", params: { id: hit.id } });
        break;
      case "device":
        if (hit.accountId) navigate({ to: "/customers/$id", params: { id: hit.accountId } });
        break;
      case "contact":
        navigate({ to: "/contacts", search: { contact: hit.id } as never });
        break;
      case "task":
        navigate({ to: "/tasks", search: { task: hit.id } as never });
        break;
      case "product":
        navigate({ to: "/inventory", search: { item: hit.id } as never });
        break;
      case "partner":
        navigate({ to: "/campaigns" });
        break;
    }
  };

  const needle = text.toLowerCase();
  const pages = kind
    ? []
    : navItems.filter((p) => !needle || p.title.toLowerCase().includes(needle));
  const actions = [
    {
      title: resolvedTheme === "dark" ? "Switch to light theme" : "Switch to dark theme",
      icon: resolvedTheme === "dark" ? Sun : Moon,
      keywords: "theme dark light mode appearance",
      run: () => setTheme(resolvedTheme === "dark" ? "light" : "dark"),
    },
  ].filter((a) => !kind && (!needle || `${a.title} ${a.keywords}`.toLowerCase().includes(needle)));

  const hits = results ?? [];
  const grouped = KIND_ORDER.map((k) => ({ kind: k, rows: hits.filter((h) => h.kind === k) })).filter(
    (g) => g.rows.length > 0,
  );
  const showRecent = !text && recent.length > 0;
  const nothing = !!text && !searching && pages.length === 0 && actions.length === 0 && hits.length === 0;

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Search"
      description="Search customers, contacts, tasks, products, partners and devices, or jump to a page."
    >
      <Command shouldFilter={false} className="**:data-[selected=true]:bg-muted **:data-selected:bg-transparent">
        <CommandInput
          value={input}
          onValueChange={setInput}
          placeholder={kind ? `Search ${KINDS[kind].heading.toLowerCase()}…` : "Search anything, or jump to a page"}
        />
        <CommandList className="max-h-96">
          {nothing && <CommandEmpty>No results for “{text}”.</CommandEmpty>}

          {showRecent && (
            <CommandGroup heading="Recent">
              {recent.map((hit) => (
                <HitRow key={`recent:${hit.id}`} hit={hit} query="" icon={History} onSelect={() => openHit(hit)} />
              ))}
            </CommandGroup>
          )}

          {grouped.map((g) => (
            <CommandGroup key={g.kind} heading={KINDS[g.kind].heading}>
              {g.rows.map((hit) => (
                <HitRow key={`${hit.kind}:${hit.id}`} hit={hit} query={text} icon={KINDS[g.kind].icon} onSelect={() => openHit(hit)} />
              ))}
            </CommandGroup>
          ))}

          {searching && (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground" role="status">
              <Loader2 className="size-4 animate-spin" /> Searching…
            </div>
          )}

          {pages.length > 0 && (
            <>
              {(showRecent || grouped.length > 0) && <CommandSeparator />}
              <CommandGroup heading="Pages">
                {pages.map((item) => {
                  const Icon = navIconMap[item.icon];
                  return (
                    <CommandItem
                      key={item.href}
                      value={`page:${item.href}`}
                      onSelect={() => {
                        close();
                        navigate({ to: item.href });
                      }}
                    >
                      <Icon />
                      <span>
                        <Highlight text={item.title} query={text} />
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </>
          )}

          {actions.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Actions">
                {actions.map((a) => (
                  <CommandItem
                    key={a.title}
                    value={`action:${a.title}`}
                    onSelect={() => {
                      close();
                      a.run();
                    }}
                  >
                    <a.icon />
                    <span>{a.title}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </CommandList>

        <div className="flex items-center justify-between gap-3 border-t px-3 py-2 text-xs text-muted-foreground">
          <span className="hidden truncate sm:inline">
            {kind ? `Searching ${KINDS[kind].heading.toLowerCase()} only` : "Narrow with  customers:  tasks:  contacts:  devices:"}
          </span>
          <span className="ml-auto flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="rounded border px-1">↑↓</kbd> move
            </span>
            <span className="flex items-center gap-1">
              <CornerDownLeft className="size-3" /> open
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border px-1">esc</kbd> close
            </span>
          </span>
        </div>
      </Command>
    </CommandDialog>
  );
}

function HitRow({
  hit,
  query,
  icon: Icon,
  onSelect,
}: {
  hit: Hit;
  query: string;
  icon: LucideIcon;
  onSelect: () => void;
}) {
  return (
    <CommandItem value={`${hit.kind}:${hit.id}`} onSelect={onSelect} className={hit.inactive ? "opacity-60" : undefined}>
      <Icon />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">
          <Highlight text={hit.title} query={query} />
        </span>
        {hit.subtitle && <span className="truncate text-xs text-muted-foreground">{hit.subtitle}</span>}
      </div>
      <ArrowRight className="size-3.5 shrink-0 text-muted-foreground opacity-0 group-data-[selected=true]/command-item:opacity-100" />
    </CommandItem>
  );
}
